/** @jsx jsx */
import { Grid } from "@cd2/components"
import { Fields, Form2 } from "@cd2/forms/build/es6"
import { fieldEvents } from "@cd2/forms/build/es6/Form2"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { P } from "@cd2/text"
import { jsx } from "@emotion/core"
import { format, startOfDay } from "date-fns"
import React, { useRef } from "react"
import { FieldsRadioCheckbox } from "../../../../Components/FieldsRadioCheckbox"
import { useGlobalState } from "../../../../Components/GlobalStateProvider"
import { FloatingBox } from "../../../../Components/Misc/FloatingBox"
import { UserSelectList } from "../../../../Components/SelectLists/UserSelectList"
import { hrAccessibleUsersFilter, useHolidayDates } from "../utils"
import { HolidayRequestStatuses } from "./HolidayRequestStatuses"

export { HolidayRequestStatuses }

export enum HolidayRequestTypes {
  PAID = "paid",
  UNPAID = "unpaid",
}

export enum HolidayRequestSourceTypes {
  HOLIDAY = "holiday",
  ABSENCE = "absence",
}

export enum HolidayRequestTimeRange {
  AM = "am",
  PM = "pm",
  ALL_DAY = "all_day",
}

export const holidayStatusOptions: { label: string; value: HolidayRequestStatuses }[] = [
  { label: "Requested", value: HolidayRequestStatuses.REQUESTED },
  { label: "Approved", value: HolidayRequestStatuses.APPROVED },
  { label: "Declined", value: HolidayRequestStatuses.DECLINED },
]
export const holidayTypeOptions: { label: string; value: HolidayRequestTypes }[] = [
  { label: "Paid Leave", value: HolidayRequestTypes.PAID },
  { label: "Unpaid Leave", value: HolidayRequestTypes.UNPAID },
]
export const holidaySourceTypeOptions: { label: string; value: HolidayRequestSourceTypes }[] = [
  { label: "Holiday", value: HolidayRequestSourceTypes.HOLIDAY },
  { label: "Sickness / Absence", value: HolidayRequestSourceTypes.ABSENCE },
]
export const holidayTimeRangeOptions: { label: string; value: HolidayRequestTimeRange }[] = [
  { label: "All day", value: HolidayRequestTimeRange.ALL_DAY },
  { label: "AM", value: HolidayRequestTimeRange.AM },
  { label: "PM", value: HolidayRequestTimeRange.PM },
]

interface IFormData {
  daysJSON: IHolidayRequestDay[]
  status: HolidayRequestStatuses
  type: HolidayRequestTypes
  user?: string
  actionedBy?: string
  requestReason?: string
  actionedReason?: string
  startDate?: string | Date | null
  endDate?: string | Date | null
}

interface IInitialData {
  daysJSON?: IHolidayRequestDay[]
  status: HolidayRequestStatuses
  type: HolidayRequestTypes
  user?: { id: string } | null
  actionedBy?: { id: string } | null
  requestReason?: string
  actionedReason?: string
  startDate?: string | Date | null
  endDate?: string | Date | null
}

export function dateOrNull(date?: Date | string | null) {
  return date ? new Date(date) : null
}
export function HolidayRequestsForm({
  initialData,
  onSubmit,
  sourceType,
}: {
  initialData?: IInitialData
  onSubmit: (data: IFormData) => Promise<void> | void
  sourceType: HolidayRequestSourceTypes
}) {
  const { currentUser } = useGlobalState()
  const formRef = useRef<FormState | null>(null)
  const skeem = window.skeem
  return (
    <React.Fragment>
      <Form2.Form
        xRef={formRef}
        initialData={{
          ...initialData,
          user: initialData?.user?.id,
          actionedBy: initialData?.actionedBy?.id ?? currentUser.id,
          type: initialData?.type ?? HolidayRequestTypes.PAID,
          startDate: dateOrNull(initialData?.startDate),
          endDate: dateOrNull(initialData?.endDate),
        }}
        onSubmit={formData => {
          const submitData = {
            ...formData,
            actionedBy:
              formData.actionedBy ??
              (formData.status !== HolidayRequestStatuses.REQUESTED
                ? skeem.currentSession.id
                : null),
          }
          return onSubmit(submitData)
        }}
      >
        <ValidateHrDates />
        <FloatingBox header="Details">
          <Grid gap={8}>
            <Form2.WithForm
              subscribe={{ dataChange: true, submitting: true }}
              render={form => {
                const errors = form.state.errors
                return (
                  <Grid
                    columns={{ repeat: { times: "auto-fit", size: { min: 200, max: { fr: 1 } } } }}
                    gap={8}
                  >
                    <Fields.Date2
                      label="Start Date"
                      onChange={form.getField("startDate").setValue}
                      value={form.getField("startDate").getValue()}
                      error={errors.startDate}
                      showRequiredStar
                    />

                    <Fields.Date2
                      label="End Date"
                      onChange={form.getField("endDate").setValue}
                      value={form.getField("endDate").getValue()}
                      error={errors.endDate}
                      showRequiredStar
                    />
                  </Grid>
                )
              }}
            />
            <DaysField />
            <ValidateUserSelect />
            <Form2.WithField<string>
              field="user"
              subscribe={true}
              render={field => (
                <UserSelectList
                  label="User"
                  onChange={field.setValue}
                  value={field.getValue()}
                  error={field.getError()}
                  filter={{
                    and: [
                      { matches: { userAccountType: "branch" } },
                      hrAccessibleUsersFilter(currentUser),
                    ].filter(Boolean),
                  }}
                  required
                />
              )}
            />
            <Form2.Select
              field="type"
              options={holidayTypeOptions}
              validations={{ presence: true }}
              valueExtractor={(opt: { value: any }) => opt.value}
            />
            {sourceType === HolidayRequestSourceTypes.HOLIDAY && (
              <Form2.Select
                field="status"
                options={holidayStatusOptions}
                validations={{ presence: true }}
                valueExtractor={(opt: { value: any }) => opt.value}
              />
            )}
          </Grid>
        </FloatingBox>
        <FloatingBox header="Approval">
          <Grid gap={8}>
            <Form2.WithField<string>
              field="actionedBy"
              render={field => (
                <UserSelectList
                  label="Approved By"
                  onChange={field.setValue}
                  value={field.getValue()}
                  filter={{
                    and: [
                      { matches: { userAccountType: "branch" } },
                      {
                        exists: {
                          path: [
                            {
                              name: "branch",
                              filter: {
                                matches: {
                                  id: currentUser.accessibleBranches.map(branch => branch.id),
                                },
                              },
                            },
                          ],
                        },
                      },
                    ],
                  }}
                />
              )}
            />
            <Form2.TextArea field="actionedReason" label="Notes" />
          </Grid>
        </FloatingBox>

        <div>
          <Form2.Submit />
        </div>
      </Form2.Form>
    </React.Fragment>
  )
}

export function ValidateHrDates() {
  const startDate = Form2.useField<Date | null>("startDate", { subscribe: true })
  const endDate = Form2.useField<Date | null>("endDate", { subscribe: true })

  Form2.useFormSubmitLock(() => {
    const startDateValue = startDate.getValue()
    const endDateValue = endDate.getValue()
    if (!startDateValue || !endDateValue) {
      if (!startDateValue) {
        startDate.setError("is required")
      }
      if (!endDateValue) {
        endDate.setError("is required")
      }
      return false
    }
    if (startDateValue.valueOf() > endDateValue.valueOf()) {
      startDate.setError("must be before end date")
      endDate.setError("must be after start date")
      return false
    }
    startDate.setError(undefined)
    endDate.setError(undefined)
  })

  return null
}

export interface IHolidayRequestDay {
  key: string
  date: string
  timeRange: HolidayRequestTimeRange
  value: 0.5 | 1
}

export function DaysField() {
  const startDateField = Form2.useField<Date | null>("startDate", { subscribe: true })
  const endDateField = Form2.useField<Date | null>("endDate", { subscribe: true })
  const dateUtils = useHolidayDates()
  const daysField = Form2.useField<IHolidayRequestDay[]>("daysJSON", {
    subscribe: true,
    defaultValue: [],
  })

  function handleDaysChange() {
    const startDate = startDateField.getValue()
    const endDate = endDateField.getValue()
    const workingDays = startDate && endDate ? dateUtils.filterWorkingDays(startDate, endDate) : []
    const currentValue = daysField.getValue()
    const daysArray = workingDays.map(workingDate => {
      const date = startOfDay(workingDate)
      const dateKey = format(date, "yyyy-MM-dd")
      const existingDay = currentValue.find(day => day.key === dateKey)
      return (
        existingDay ??
        ({
          key: dateKey,
          date: date.toJSON(),
          timeRange: HolidayRequestTimeRange.ALL_DAY,
          value: 1,
        } as IHolidayRequestDay)
      )
    })
    daysField.setValue(
      daysArray.sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        return dateA.valueOf() - dateB.valueOf()
      }),
    )
  }

  startDateField.events.on(fieldEvents.change, handleDaysChange)
  endDateField.events.on(fieldEvents.change, handleDaysChange)

  function handleDayChange(key: string, data: Partial<IHolidayRequestDay>) {
    const currentValue = daysField.getValue()
    const dayIndex = currentValue.findIndex(day => day.key === key)
    if (dayIndex === -1) {
      return
    }
    const newDay = { ...currentValue[dayIndex], ...data, key }
    currentValue[dayIndex] = newDay
    daysField.setValue([...currentValue])
  }

  const days = daysField.getValue()

  return (
    <div>
      <Grid columns={{ repeat: { times: "auto-fill", size: { min: 180, max: 300 } } }} gap={8}>
        {days.map(day => (
          <FloatingBox key={day.key}>
            <Grid
              columns={[{ fr: 1 }, { fr: 0.8 }]}
              gap={8}
              alignItems="center"
              justifyItems="center"
            >
              <div>
                <P weight={600}>{format(new Date(day.date), "EEEE")}</P>
                <P>{format(new Date(day.date), "do LLLL yyyy")}</P>
              </div>
              <Grid>
                {holidayTimeRangeOptions.map(opt => (
                  <FieldsRadioCheckbox
                    key={opt.value}
                    name={`${day.key}-${opt.value}`}
                    label={opt.label}
                    onChange={() =>
                      handleDayChange(day.key, {
                        timeRange: opt.value,
                        value: opt.value === HolidayRequestTimeRange.ALL_DAY ? 1 : 0.5,
                      })
                    }
                    value={opt.value === day.timeRange}
                  />
                ))}
              </Grid>
            </Grid>
          </FloatingBox>
        ))}
      </Grid>
    </div>
  )
}

function ValidateUserSelect() {
  Form2.useFormSubmitLock(state => {
    const field = state.getField("user")
    if (!field.getValue()) {
      field.setError("must be present")
      return false
    }
    field.setError(undefined)
  })
  return null
}
